import React from "react";
import { Link } from "react-router-dom";
import { FileText, Wrench, Megaphone, ArrowRight } from "lucide-react";

const TYPE_CONFIG = {
    blog: { icon: FileText, accent: "from-blue-500 to-blue-600", bg: "bg-blue-50", label: "Blog", text: "text-blue-700" },
    guide: { icon: FileText, accent: "from-emerald-500 to-emerald-600", bg: "bg-emerald-50", label: "Navod", text: "text-emerald-700" },
    service: { icon: Wrench, accent: "from-amber-500 to-amber-600", bg: "bg-amber-50", label: "Sluzba", text: "text-amber-700" },
    banner: { icon: Megaphone, accent: "from-purple-500 to-purple-600", bg: "bg-purple-50", label: "Akce", text: "text-purple-700" },
};

export default function PromoCard({ card, viewMode = "grid" }) {
    if (!card) return null;
    const cfg = TYPE_CONFIG[card.card_type] || TYPE_CONFIG.blog;
    const Icon = cfg.icon;
    const isExternal = card.link_url?.startsWith("http");
    const Wrapper = isExternal ? "a" : Link;
    const wrapperProps = isExternal
        ? { href: card.link_url, target: "_blank", rel: "noreferrer" }
        : { to: card.link_url || "#" };

    if (viewMode === "list") {
        return (
            <Wrapper {...wrapperProps}
                className="flex items-center gap-4 bg-gradient-to-r from-gray-50 to-white border-2 border-dashed border-gray-200 rounded-xl p-4 hover:border-green-300 hover:shadow-sm transition-all group"
                data-testid={`promo-listing-card-${card.id}`}>
                {card.image_url ? (
                    <img src={card.image_url} alt="" className="w-16 h-16 rounded-lg object-cover flex-shrink-0" />
                ) : (
                    <div className={`w-16 h-16 rounded-lg bg-gradient-to-br ${cfg.accent} flex items-center justify-center flex-shrink-0`}>
                        <Icon size={24} className="text-white" />
                    </div>
                )}
                <div className="flex-1 min-w-0">
                    <span className={`text-[10px] font-bold uppercase tracking-wider ${cfg.text}`}>{cfg.label}</span>
                    <p className="text-sm font-bold text-gray-800 mt-0.5 group-hover:text-green-700 transition-colors">{card.title}</p>
                    {card.description && <p className="text-xs text-gray-500 mt-0.5 truncate">{card.description}</p>}
                </div>
                <ArrowRight size={16} className="text-gray-300 group-hover:text-green-500 transition-colors flex-shrink-0" />
            </Wrapper>
        );
    }

    // Grid mode – match product card height
    if (card.card_type === "banner" && card.image_url) {
        return (
            <Wrapper {...wrapperProps}
                className="block rounded-xl overflow-hidden border-2 border-dashed border-gray-200 hover:border-green-300 hover:shadow-md transition-all group relative h-full"
                data-testid={`promo-listing-card-${card.id}`}>
                <img src={card.image_url} alt={card.title} className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent flex flex-col justify-end p-4">
                    <span className="text-[10px] font-bold text-white/80 uppercase tracking-wider">{cfg.label}</span>
                    <p className="text-sm font-bold text-white mt-0.5">{card.title}</p>
                </div>
            </Wrapper>
        );
    }

    return (
        <Wrapper {...wrapperProps}
            className="flex flex-col rounded-xl border-2 border-dashed border-gray-200 hover:border-green-300 hover:shadow-md transition-all group overflow-hidden h-full"
            data-testid={`promo-listing-card-${card.id}`}>
            {card.image_url ? (
                <div className="aspect-square bg-gray-50 overflow-hidden flex-shrink-0">
                    <img src={card.image_url} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                </div>
            ) : (
                <div className={`aspect-square bg-gradient-to-br ${cfg.accent} flex items-center justify-center flex-shrink-0`}>
                    <Icon size={36} className="text-white/80" />
                </div>
            )}
            <div className="p-3 flex flex-col flex-1">
                <span className={`text-[10px] font-bold uppercase tracking-wider ${cfg.text}`}>{cfg.label}</span>
                <p className="text-sm font-bold text-gray-800 mt-1 leading-tight group-hover:text-green-700 transition-colors line-clamp-2">{card.title}</p>
                {card.description && <p className="text-xs text-gray-500 mt-1 line-clamp-2">{card.description}</p>}
                <span className="inline-flex items-center gap-1 text-xs text-green-600 font-semibold mt-auto pt-2 group-hover:text-green-700">
                    Zjistit vice <ArrowRight size={11} />
                </span>
            </div>
        </Wrapper>
    );
}
